import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'General Cleaning',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4puqmpfcjnktac9qm00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: 'General Cleaning',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 text-center mb-16">
        <h2 className="text-5xl font-bold text-gray-900 mb-6">
          Ready to Experience Professional Cleaning?
        </h2>
        <p className="text-xl text-gray-600 mb-8">
          Contact us today for a free consultation and quote
        </p>
        <a 
          href="tel:+97336096336" 
          className="inline-flex items-center gap-3 bg-teal-500 text-white px-10 py-5 rounded-full text-lg font-medium hover:bg-teal-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
        >
          <i className="ri-phone-line text-2xl"></i>
          Call +973 36096336
          <i className="ri-arrow-right-line"></i>
        </a>
        <p className="mt-6 text-gray-600">
          <a 
            href="#contact-form" 
            className="text-teal-500 hover:text-teal-600 font-medium inline-flex items-center gap-1 cursor-pointer"
          >
            Or send us a message <i className="ri-arrow-right-line"></i>
          </a>
        </p>
      </div>
      
      <div className="max-w-3xl mx-auto px-6">
        <form 
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-gray-50 rounded-3xl p-10 shadow-lg"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Send Us a Message
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input 
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" 
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input 
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" 
                placeholder="your@email.com"
              />
            </div>
          </div>
          
          <div className="mb-6">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input 
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" 
              placeholder="+973 XXXX XXXX"
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
              Service Interested In
            </label>
            <select 
              id="service"
              name="service"
              value={formData.service}
              onChange={(e) => setFormData({...formData, service: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="General Cleaning">General Cleaning</option>
              <option value="Deep Cleaning">Deep Cleaning</option>
              <option value="Pest Control">Pest Control</option>
              <option value="Office Services">Office Services</option>
              <option value="Specialized Cleaning">Specialized Cleaning</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message (Max 500 characters)
            </label>
            <textarea 
              id="message"
              name="message"
              required
              maxLength={500}
              rows={5}
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none" 
              placeholder="Tell us about your cleaning needs..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
          </div>
          
          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-500 text-white py-4 rounded-lg font-medium hover:bg-teal-600 transition-colors text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>

          {submitStatus === 'success' && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm text-center">
              Thank you! Your message has been sent successfully.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
              Sorry, there was an error sending your message. Please try again.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
